import React, { useState, useEffect } from 'react';
import { 
  Plus,
  Search,
  Filter,
  ChevronDown,
  DollarSign,
  TrendingUp,
  Target,
  Calculator,
  Plane
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { BudgetingService } from '../../lib/budgetingService';
import { BudgetDashboard } from './BudgetDashboard';
import { MonthlySummary } from './MonthlySummary';
import { YearlySummary } from './YearlySummary';
import { Transactions } from './Transactions';
import { SavingsGoals } from './SavingsGoals';
export function BudgetingManagement() {
  const { user } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'dashboard' | 'monthly' | 'yearly' | 'transactions' | 'savings-goals'>('dashboard');
  const [toasts, setToasts] = useState<{ id: number; type: 'success' | 'error'; message: string }[]>([]);

  useEffect(() => {
    const onOpenModal = (e: any) => {
      if (e?.detail) setActiveModal(e.detail);
    };
    window.addEventListener('budget:open-modal', onOpenModal);
    const onToast = (e: any) => {
      if (!e?.detail) return;
      const id = Date.now();
      setToasts(prev => [...prev, { id, type: e.detail.type, message: e.detail.message }]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
    };
    window.addEventListener('budget:toast', onToast);

    return () => {
      window.removeEventListener('budget:open-modal', onOpenModal);
      window.removeEventListener('budget:toast', onToast);
    };
  }, []);

  const budgetActions = [
    {
      id: 'income',
      label: 'Add Income',
      icon: <TrendingUp className="w-4 h-4" />,
      description: 'Add salary, freelance, or other income'
    },
    {
      id: 'expense',
      label: 'Add Expense',
      icon: <DollarSign className="w-4 h-4" />,
      description: 'Add monthly bills or one-time expenses'
    },
    {
      id: 'savings',
      label: 'Add Savings Goal',
      icon: <Target className="w-4 h-4" />,
      description: 'Set a new financial goal'
    },
    {
      id: 'budget',
      label: 'Create Budget',
      icon: <Calculator className="w-4 h-4" />,
      description: 'Create a new monthly budget'
    },
    {
      id: 'estimate',
      label: 'Estimate Expense',
      icon: <Plane className="w-4 h-4" />,
      description: 'Plan for trips, events, or purchases'
    }
  ];

  const handleActionClick = (actionId: string) => {
    setActiveModal(actionId);
    setShowDropdown(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Budgeting</h1>
              <p className="text-gray-600 mt-1">Manage your finances and plan for the future</p>
            </div>
            <div className="relative">
              <button 
                onClick={() => setShowDropdown(!showDropdown)}
                className="bg-idecide-primary text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-idecide-primary/90 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span>Add Budget Item</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {showDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border z-50">
                  <div className="py-2">
                    {budgetActions.map((action) => (
                      <button
                        key={action.id}
                        onClick={() => handleActionClick(action.id)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-start space-x-3"
                      >
                        <div className="text-idecide-primary mt-0.5">
                          {action.icon}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{action.label}</div>
                          <div className="text-sm text-gray-500">{action.description}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Tabs */}
          <div className="mt-4 flex items-center space-x-2">
            <button
              onClick={() => setActiveView('dashboard')}
              className={`px-3 py-1.5 rounded-md text-sm ${activeView === 'dashboard' ? 'bg-idecide-primary text-white' : 'text-gray-700 hover:bg-gray-100'}`}
            >Dashboard</button>
            <button
              onClick={() => setActiveView('transactions')}
              className={`px-3 py-1.5 rounded-md text-sm ${activeView === 'transactions' ? 'bg-idecide-primary text-white' : 'text-gray-700 hover:bg-gray-100'}`}
            >Transactions</button>
            <button
              onClick={() => setActiveView('monthly')}
              className={`px-3 py-1.5 rounded-md text-sm ${activeView === 'monthly' ? 'bg-idecide-primary text-white' : 'text-gray-700 hover:bg-gray-100'}`}
            >Monthly</button>
            <button
              onClick={() => setActiveView('yearly')}
              className={`px-3 py-1.5 rounded-md text-sm ${activeView === 'yearly' ? 'bg-idecide-primary text-white' : 'text-gray-700 hover:bg-gray-100'}`}
            >Yearly</button>
            <button
              onClick={() => setActiveView('savings-goals')}
              className={`px-3 py-1.5 rounded-md text-sm ${activeView === 'savings-goals' ? 'bg-idecide-primary text-white' : 'text-gray-700 hover:bg-gray-100'}`}
            >Savings Goals</button>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search budget items..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            />
          </div>
          <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2">
            <Filter className="w-4 h-4" />
            <span>Filters</span>
          </button>
        </div>
      </div>

      {/* Main Content - Full Width */}
      <div className="p-6">
        {activeView === 'dashboard' && <BudgetDashboard />}
        {activeView === 'transactions' && <Transactions />}
        {activeView === 'monthly' && <MonthlySummary />}
        {activeView === 'yearly' && <YearlySummary />}
        {activeView === 'savings-goals' && <SavingsGoals />}
      </div>

      {/* Modals */}
      {activeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            {activeModal === 'income' && <AddIncomeModal onClose={() => setActiveModal(null)} />}
            {activeModal === 'expense' && <AddExpenseModal onClose={() => setActiveModal(null)} />}
            {activeModal === 'savings' && <AddSavingsGoalModal onClose={() => setActiveModal(null)} />}
            {activeModal === 'budget' && <CreateBudgetModal onClose={() => setActiveModal(null)} />}
            {activeModal === 'estimate' && <EstimateExpenseModal onClose={() => setActiveModal(null)} />}
          </div>
        </div>
      )}
      {/* Toasts */}
      <div className="fixed bottom-4 right-4 space-y-2 z-50">
        {toasts.map(t => (
          <div key={t.id} className={`px-4 py-3 rounded-lg shadow ${t.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
}

// Modal Components
function AddIncomeModal({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    category: 'Salary',
    frequency: 'monthly'
  });
  const [categories, setCategories] = useState<any[]>([]);
  useEffect(() => {
    (async () => {
      if (!user?.id) return;
      try {
        const cats = await BudgetingService.getBudgetCategories(user.id);
        setCategories(cats.filter(c => c.type === 'income'));
      } catch {}
    })();
  }, [user?.id]);
  const [errors, setErrors] = useState<{ amount?: string; description?: string }>();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amt = Number(formData.amount);
    const errs: any = {};
    if (!amt || amt <= 0) errs.amount = 'Enter a positive amount';
    if (!formData.description.trim()) errs.description = 'Description is required';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    if (!user?.id) return onClose();
    try {
      // Ensure there is an active budget
      let activeBudget = await BudgetingService.getActiveBudget(user.id);
      if (!activeBudget) {
        activeBudget = await BudgetingService.createBudget({
          user_id: user.id,
          name: 'Default Budget',
          description: 'Auto-created budget',
          period_type: 'monthly',
          start_date: new Date().toISOString().split('T')[0],
          total_income: 0,
          total_expenses: 0,
          total_savings: 0,
          is_active: true
        } as any);
      }

      // Find or create category
      const categories = await BudgetingService.getBudgetCategories(user.id);
      let category = categories.find(c => c.name === formData.category);
      if (!category) {
        category = await BudgetingService.createBudgetCategory({
          user_id: user.id,
          name: formData.category,
          type: 'income',
          color: '#16a34a',
          icon: 'dollar-sign'
        } as any);
      }

      // Create transaction
      await BudgetingService.createBudgetTransaction({
        user_id: user.id,
        budget_id: activeBudget.id,
        category_id: category.id,
        amount: Number(formData.amount),
        type: 'income' as any,
        description: formData.description,
        transaction_date: new Date().toISOString().split('T')[0],
        payment_method: 'bank_transfer',
        tags: ['manual'],
        is_recurring: formData.frequency !== 'one-time',
        recurring_frequency: formData.frequency !== 'one-time' ? (formData.frequency as any) : null
      } as any);

      window.dispatchEvent(new CustomEvent('budget:refresh'));
      window.dispatchEvent(new CustomEvent('budget:toast', { detail: { type: 'success', message: 'Income added' } }));
    } catch (err) {
      console.error('Failed to add income', err);
      window.dispatchEvent(new CustomEvent('budget:toast', { detail: { type: 'error', message: 'Failed to add income' } }));
    }
    onClose();
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Add Income</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
          <input
            type="number"
            value={formData.amount}
            onChange={(e) => setFormData({...formData, amount: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            placeholder="0.00"
            required
          />
          {errors?.amount && <p className="text-sm text-red-600 mt-1">{errors.amount}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            placeholder="e.g., Salary, Freelance work"
            required
          />
          {errors?.description && <p className="text-sm text-red-600 mt-1">{errors.description}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({...formData, category: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
          >
            {categories.length > 0 ? (
              categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)
            ) : (
              <>
                <option value="Salary">Salary</option>
                <option value="Freelance">Freelance</option>
                <option value="Investment">Investment</option>
                <option value="Other">Other</option>
              </>
            )}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
          <select
            value={formData.frequency}
            onChange={(e) => setFormData({...formData, frequency: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
          >
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="yearly">Yearly</option>
            <option value="one-time">One-time</option>
          </select>
        </div>
        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            className="flex-1 bg-idecide-primary text-white py-2 px-4 rounded-lg hover:bg-idecide-primary/90"
          >
            Add Income
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function AddExpenseModal({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    category: 'Housing',
    frequency: 'monthly'
  });
  const [categories, setCategories] = useState<any[]>([]);
  useEffect(() => {
    (async () => {
      if (!user?.id) return;
      try {
        const cats = await BudgetingService.getBudgetCategories(user.id);
        setCategories(cats.filter(c => c.type === 'expense'));
      } catch {}
    })();
  }, [user?.id]);
  const [errors, setErrors] = useState<{ amount?: string; description?: string }>();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amt = Number(formData.amount);
    const errs: any = {};
    if (!amt || amt <= 0) errs.amount = 'Enter a positive amount';
    if (!formData.description.trim()) errs.description = 'Description is required';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    if (!user?.id) return onClose();
    try {
      // Ensure there is an active budget
      let activeBudget = await BudgetingService.getActiveBudget(user.id);
      if (!activeBudget) {
        activeBudget = await BudgetingService.createBudget({
          user_id: user.id,
          name: 'Default Budget',
          description: 'Auto-created budget',
          period_type: 'monthly',
          start_date: new Date().toISOString().split('T')[0],
          total_income: 0,
          total_expenses: 0,
          total_savings: 0,
          is_active: true
        } as any);
      }

      // Find or create category
      const categories = await BudgetingService.getBudgetCategories(user.id);
      let category = categories.find(c => c.name === formData.category);
      if (!category) {
        category = await BudgetingService.createBudgetCategory({
          user_id: user.id,
          name: formData.category,
          type: 'expense',
          color: '#ef4444',
          icon: 'shopping-cart'
        } as any);
      }

      await BudgetingService.createBudgetTransaction({
        user_id: user.id,
        budget_id: activeBudget.id,
        category_id: category.id,
        amount: Number(formData.amount),
        type: 'expense' as any,
        description: formData.description,
        transaction_date: new Date().toISOString().split('T')[0],
        payment_method: 'card',
        tags: ['manual'],
        is_recurring: formData.frequency !== 'one-time',
        recurring_frequency: formData.frequency !== 'one-time' ? (formData.frequency as any) : null
      } as any);

      window.dispatchEvent(new CustomEvent('budget:refresh'));
      window.dispatchEvent(new CustomEvent('budget:toast', { detail: { type: 'success', message: 'Expense added' } }));
    } catch (err) {
      console.error('Failed to add expense', err);
      window.dispatchEvent(new CustomEvent('budget:toast', { detail: { type: 'error', message: 'Failed to add expense' } }));
    }
    onClose();
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Add Expense</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
          <input
            type="number"
            value={formData.amount}
            onChange={(e) => setFormData({...formData, amount: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            placeholder="0.00"
            required
          />
          {errors?.amount && <p className="text-sm text-red-600 mt-1">{errors.amount}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            placeholder="e.g., Rent, Groceries, Utilities"
            required
          />
          {errors?.description && <p className="text-sm text-red-600 mt-1">{errors.description}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({...formData, category: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
          >
            {categories.length > 0 ? (
              categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)
            ) : (
              <>
                <option value="Housing">Housing</option>
                <option value="Food & Dining">Food & Dining</option>
                <option value="Transportation">Transportation</option>
                <option value="Entertainment">Entertainment</option>
                <option value="Utilities">Utilities</option>
                <option value="Other">Other</option>
              </>
            )}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
          <select
            value={formData.frequency}
            onChange={(e) => setFormData({...formData, frequency: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
          >
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="yearly">Yearly</option>
            <option value="one-time">One-time</option>
          </select>
        </div>
        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            className="flex-1 bg-idecide-primary text-white py-2 px-4 rounded-lg hover:bg-idecide-primary/90"
          >
            Add Expense
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function AddSavingsGoalModal({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    targetAmount: '',
    currentAmount: '0',
    targetDate: '',
    category: 'Emergency Fund'
  });
  const [errors, setErrors] = useState<{ name?: string; targetAmount?: string; targetDate?: string }>();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amt = Number(formData.targetAmount);
    const errs: any = {};
    if (!formData.name.trim()) errs.name = 'Name is required';
    if (!amt || amt <= 0) errs.targetAmount = 'Enter a positive target';
    if (!formData.targetDate) errs.targetDate = 'Target date required';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    if (!user?.id) return onClose();
    try {
      await BudgetingService.createSavingsGoal({
        user_id: user.id,
        name: formData.name,
        description: '',
        target_amount: Number(formData.targetAmount),
        current_amount: Number(formData.currentAmount || '0'),
        target_date: formData.targetDate,
        priority: 'medium',
        category: formData.category.toLowerCase().includes('emergency') ? 'emergency' : 'general',
        is_achieved: false
      } as any);

      window.dispatchEvent(new CustomEvent('budget:refresh'));
      window.dispatchEvent(new CustomEvent('budget:toast', { detail: { type: 'success', message: 'Goal added' } }));
    } catch (err) {
      console.error('Failed to add savings goal', err);
      window.dispatchEvent(new CustomEvent('budget:toast', { detail: { type: 'error', message: 'Failed to add goal' } }));
    }
    onClose();
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Add Savings Goal</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Goal Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            placeholder="e.g., Emergency Fund, Vacation"
            required
          />
          {errors?.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target Amount</label>
          <input
            type="number"
            value={formData.targetAmount}
            onChange={(e) => setFormData({...formData, targetAmount: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            placeholder="0.00"
            required
          />
          {errors?.targetAmount && <p className="text-sm text-red-600 mt-1">{errors.targetAmount}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Current Amount</label>
          <input
            type="number"
            value={formData.currentAmount}
            onChange={(e) => setFormData({...formData, currentAmount: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            placeholder="0.00"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target Date</label>
          <input
            type="date"
            value={formData.targetDate}
            onChange={(e) => setFormData({...formData, targetDate: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            required
          />
          {errors?.targetDate && <p className="text-sm text-red-600 mt-1">{errors.targetDate}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({...formData, category: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
          >
            <option value="Emergency Fund">Emergency Fund</option>
            <option value="Vacation">Vacation</option>
            <option value="Home Purchase">Home Purchase</option>
            <option value="Retirement">Retirement</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            className="flex-1 bg-idecide-primary text-white py-2 px-4 rounded-lg hover:bg-idecide-primary/90"
          >
            Add Goal
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function CreateBudgetModal({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    period: 'monthly',
    startDate: new Date().toISOString().split('T')[0]
  });
  const [errors, setErrors] = useState<{ name?: string }>();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: any = {};
    if (!formData.name.trim()) errs.name = 'Name is required';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    if (!user?.id) return onClose();
    try {
      await BudgetingService.createBudget({
        user_id: user.id,
        name: formData.name,
        description: '',
        period_type: formData.period as any,
        start_date: formData.startDate,
        total_income: 0,
        total_expenses: 0,
        total_savings: 0,
        is_active: true
      } as any);

      window.dispatchEvent(new CustomEvent('budget:refresh'));
      window.dispatchEvent(new CustomEvent('budget:toast', { detail: { type: 'success', message: 'Budget created' } }));
    } catch (err) {
      console.error('Failed to create budget', err);
      window.dispatchEvent(new CustomEvent('budget:toast', { detail: { type: 'error', message: 'Failed to create budget' } }));
    }
    onClose();
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Create Budget</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Budget Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            placeholder="e.g., Monthly Budget 2024"
            required
          />
          {errors?.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
          <select
            value={formData.period}
            onChange={(e) => setFormData({...formData, period: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
          >
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({...formData, startDate: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            required
          />
        </div>
        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            className="flex-1 bg-idecide-primary text-white py-2 px-4 rounded-lg hover:bg-idecide-primary/90"
          >
            Create Budget
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function EstimateExpenseModal({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    type: 'travel',
    description: '',
    estimatedAmount: '',
    date: '',
    location: ''
  });
  const [errors, setErrors] = useState<{ description?: string; estimatedAmount?: string }>();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amt = Number(formData.estimatedAmount);
    const errs: any = {};
    if (!formData.description.trim()) errs.description = 'Description is required';
    if (!amt || amt <= 0) errs.estimatedAmount = 'Enter a positive estimate';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    if (!user?.id) return onClose();
    try {
      await BudgetingService.createExpenseEstimator({
        user_id: user.id,
        type: formData.type as any,
        description: formData.description,
        estimated_amount: Number(formData.estimatedAmount),
        planned_date: formData.date || null,
        location: formData.location || null,
        status: 'planned'
      } as any);

      window.dispatchEvent(new CustomEvent('budget:refresh'));
      window.dispatchEvent(new CustomEvent('budget:toast', { detail: { type: 'success', message: 'Estimate added' } }));
    } catch (err) {
      console.error('Failed to add estimator', err);
      window.dispatchEvent(new CustomEvent('budget:toast', { detail: { type: 'error', message: 'Failed to add estimate' } }));
    }
    onClose();
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Estimate Expense</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({...formData, type: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
          >
            <option value="travel">Travel</option>
            <option value="event">Event</option>
            <option value="purchase">Purchase</option>
            <option value="dining">Dining</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            placeholder="e.g., Weekend trip to Melbourne"
            required
          />
          {errors?.description && <p className="text-sm text-red-600 mt-1">{errors.description}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Amount</label>
          <input
            type="number"
            value={formData.estimatedAmount}
            onChange={(e) => setFormData({...formData, estimatedAmount: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            placeholder="0.00"
            required
          />
          {errors?.estimatedAmount && <p className="text-sm text-red-600 mt-1">{errors.estimatedAmount}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({...formData, date: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Location (Optional)</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({...formData, location: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
            placeholder="e.g., Melbourne, VIC"
          />
        </div>
        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            className="flex-1 bg-idecide-primary text-white py-2 px-4 rounded-lg hover:bg-idecide-primary/90"
          >
            Add Estimate
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
